import type { EndpointWithResources } from '@/types/endpoint';
import type { App } from '@/types/app';
import type { Deployment } from '@/types/deployment';
import type { Instance } from '@/types/instance';
import type { TLSSettings } from '@/types/tlsSettings';
import type { DomainDetail } from '@/types/domain';
import { endpointCapacity } from './dashboardCompute.ts';
import { capacityTone } from './capacity.ts';

export type AttentionSeverity = 'danger' | 'warning';

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  title: string;
  detail?: string;
  to: string;
}

export interface AttentionInput {
  endpoints: EndpointWithResources[];
  apps: App[];
  deployments: Deployment[];
  instances: Instance[];
  tls: TLSSettings | null;
  /** Domain details (certificates are per domain); null when they could not be loaded. */
  domains: Pick<DomainDetail, 'id' | 'name' | 'new_app_tls_mode' | 'certificate'>[] | null;
  /** ms epoch, injectable for tests. */
  now?: number;
}

const FAILED_WINDOW_HOURS = 24;
const FAILED_WINDOW_MS = FAILED_WINDOW_HOURS * 60 * 60 * 1000;
const CERT_WARN_DAYS = 14;

function arr<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : [];
}

function endpointLink(id: number): string {
  return `/resources/endpoints/${id}`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

export function computeAttention(input: AttentionInput): AttentionItem[] {
  const endpoints = arr(input.endpoints);
  const apps = arr(input.apps);
  const deployments = arr(input.deployments);
  const instances = arr(input.instances);
  const tls = input.tls ?? null;
  const domains = arr(input.domains);
  const now = input.now ?? Date.now();
  const items: AttentionItem[] = [];

  // Endpoints we cannot act on (down or unknown): their own item explains
  // everything else, so capacity/app items for them would be cascade noise.
  const unreachable = new Set(endpoints.filter(e => e.status === 'down' || e.status === 'unknown').map(e => e.id));

  // 1. Endpoint status
  for (const ep of endpoints) {
    const to = endpointLink(ep.id);
    if (ep.status === 'down') {
      items.push({ id: `endpoint-down-${ep.id}`, severity: 'danger', title: `Endpoint ${ep.name} is down`, to });
    } else if (ep.status === 'unknown') {
      items.push({ id: `endpoint-unknown-${ep.id}`, severity: 'warning', title: `Endpoint ${ep.name} status unknown`, to });
    }
  }

  // 2. Capacity (limiting resource is the first with the highest percent)
  for (const c of endpointCapacity(endpoints, apps)) {
    if (!c.hasResources) continue;
    // Suppressed for unreachable endpoints: usage figures are stale/meaningless.
    if (unreachable.has(c.id)) continue;
    // Drop non-finite percents (NaN from missing usage) so one bad metric
    // doesn't hide the others.
    const resources = ([
      ['cpu', c.cpu.percent],
      ['memory', c.memory.percent],
      ['storage', c.storage.percent],
    ] as [string, number][]).filter(([, pct]) => Number.isFinite(pct));
    if (resources.length === 0) continue;
    let [limiting, p] = resources[0];
    for (const [name, percent] of resources) {
      if (percent > p) { limiting = name; p = percent; }
    }
    const tone = capacityTone(p);
    if (tone === 'ok') continue;
    items.push({
      id: `capacity-${c.id}`,
      severity: tone === 'danger' ? 'danger' : 'warning',
      title: `Endpoint ${c.name} is at ${p}% capacity`,
      detail: limiting,
      to: endpointLink(c.id),
    });
  }

  // 3. Apps
  for (const app of apps) {
    // Suppressed when its endpoint is down/unknown: the endpoint item already explains it.
    if (app.endpoint && unreachable.has(app.endpoint.id)) continue;
    const label = app.display_name || app.name;
    if (app.status === 'failed') {
      items.push({ id: `app-failed-${app.id}`, severity: 'danger', title: `App ${label} failed`, to: `/apps/${app.id}` });
    } else if (app.status === 'unhealthy') {
      items.push({ id: `app-unhealthy-${app.id}`, severity: 'warning', title: `App ${label} is unhealthy`, to: `/apps/${app.id}` });
    }
  }

  // 4. Failed deployments in the window, grouped. Invalid dates are outside the window.
  // `rolled_back` is intentionally not counted as a failure: the system recovered.
  const failed = deployments.filter(d => {
    if (d.status !== 'failed') return false;
    const t = Date.parse(d.created_at);
    return Number.isFinite(t) && t >= now - FAILED_WINDOW_MS;
  }).length;
  if (failed > 0) {
    items.push({
      id: 'deployments-failed',
      severity: 'danger',
      title: `${plural(failed, 'deployment')} failed in the last ${FAILED_WINDOW_HOURS}h`,
      to: '/apps?tab=deployments',
    });
  }

  // 5. Instance sync (inactive instances are intentionally ignored)
  for (const inst of instances) {
    if (inst.is_active && inst.last_sync_status === 'failed') {
      items.push({
        id: `instance-sync-${inst.id}`,
        severity: 'warning',
        title: `Sync failed for instance ${inst.name}`,
        detail: inst.last_sync_error ?? undefined,
        to: '/instances',
      });
    }
  }

  // 6. TLS: origin certificates are per domain, endpoint state is global.
  const to = '/settings/domains';
  for (const d of domains) {
    const days = d.certificate?.expires_in_days;
    if (typeof days === 'number' && Number.isFinite(days)) {
      // expires_in_days = floor(hours/24): 0 means <24h left (not yet expired),
      // -1 means expired within the last day.
      const id = `tls-cert-${d.id}`;
      const what = `Origin certificate for ${d.name}`;
      if (days < 0) {
        items.push({ id, severity: 'danger', title: `${what} expired`, to });
      } else if (days === 0) {
        items.push({ id, severity: 'danger', title: `${what} expires within 24 hours`, to });
      } else if (days <= CERT_WARN_DAYS) {
        items.push({ id, severity: 'warning', title: `${what} expires in ${plural(days, 'day')}`, to });
      }
    }
    if (d.new_app_tls_mode === 'origin' && d.certificate == null) {
      items.push({
        id: `tls-no-cert-${d.id}`,
        severity: 'warning',
        title: `New apps on ${d.name} use origin TLS but no certificate is uploaded`,
        to,
      });
    }
  }
  if (tls) {
    const originMode = domains.some((d) => d.new_app_tls_mode === 'origin');
    for (const e of arr(tls.endpoints)) {
      if (
        (e.effective_status === 'error' || e.effective_status === 'outdated') &&
        (originMode || e.origin_apps > 0)
      ) {
        items.push({
          id: `tls-endpoint-${e.endpoint_id}`,
          severity: 'warning',
          title: `TLS on endpoint ${e.name} is ${e.effective_status}`,
          to,
        });
      }
    }
  }

  // 7. Danger first; Array.prototype.sort is stable.
  const rank = (s: AttentionSeverity) => (s === 'danger' ? 0 : 1);
  return items.sort((a, b) => rank(a.severity) - rank(b.severity));
}
